/**
 * The briefs' questions for the consultant (2026-09-28): what each "Ask" on a brief drafts, and the context it carries
 * (lib/copilot/ask). The overall Plant brief asks about a defect and its evidence; a period's brief about the period,
 * a defect's rate, a mover, an excursion, a risk and what to do. A brief shown in a report (the AI report, the brief of
 * a window) asks from the report, with its window's days. The consultant's page offers the overall brief's defects as
 * quick questions. The questions ask what is behind a number and what to check, never assert what caused it. Pure.
 */
import type {
  BriefDefect,
  BriefDriver,
  CadenceBrief,
  CadenceExcursion,
  CadenceMover,
  CadenceParetoRow,
  CadenceRisk,
  PlantBrief,
} from "@/lib/api/brief";
import { aboutFor, itemLabel, type AboutInput, type CopilotAbout } from "@/lib/copilot/ask";
import {
  CADENCE_NOUN,
  dayRangeText,
  excursionTitle,
  excursionWay,
  excursionWindow,
  lineName,
  windowLabel,
  type ActionBucket,
  type BriefPeriodRef,
} from "./cadence-words";

/** A question to draft in the consultant's composer, and what it is about. */
export interface Ask {
  question: string;
  about: CopilotAbout;
}

/** The overall brief's name on the chip. */
export const PLANT_BRIEF_LABEL = "Plant brief";

/** The overall brief itself: the page's context on the Overall tab. */
export function overallAbout(): CopilotAbout {
  return aboutFor({ page: "brief", cadence: "overall", label: PLANT_BRIEF_LABEL });
}

/**
 * Plant words that name a part of the plant or its records as much as a defect: a plant that books mould shift as
 * "Shift" would be asked "What drives Shift?", which reads as the work shift (Arjun, 2026-10-06).
 */
const AMBIGUOUS_DEFECT_WORDS = new Set([
  "shift", "line", "heat", "lot", "batch", "part", "day", "week", "month", "mould", "mold", "core", "pour", "ladle",
  "sand", "metal", "iron", "time", "scrap",
]);

/**
 * A defect as a question names it: the plant's own word, or, when that word also names a part of the plant or its
 * records, the defect's standard name with the plant's word after it ("mould shift (Shift)"). A defect keyed by the
 * plant's word itself has no other name to give, and keeps it.
 */
export function defectQuestionName(d: Pick<BriefDefect, "defect" | "label">): string {
  const word = d.label.trim();
  const standard = d.defect.replace(/[_-]+/g, " ").trim();
  if (!AMBIGUOUS_DEFECT_WORDS.has(word.toLowerCase()) || !standard || standard.toLowerCase() === word.toLowerCase()) {
    return d.label;
  }
  return `${standard} (${word})`;
}

/** A defect of the overall brief: its card's header, and a quick question on the consultant's page. */
export function overallDefectAbout(d: Pick<BriefDefect, "defect" | "label">): CopilotAbout {
  return aboutFor({ page: "brief", cadence: "overall", defect: d.defect, label: itemLabel(PLANT_BRIEF_LABEL, d.label) });
}

/** The ask on a defect's card of the overall brief. */
export function overallDefectAsk(d: Pick<BriefDefect, "defect" | "label">): Ask {
  return {
    question: `What drives ${defectQuestionName(d)} in this plant, and what should we check first?`,
    about: overallDefectAbout(d),
  };
}

/** An evidence row's context: its defect and the condition the evidence tests (the row's question is the brief's own). */
export function evidenceAbout(d: Pick<BriefDefect, "defect" | "label">, driver: Pick<BriefDriver, "condition">): CopilotAbout {
  return aboutFor({
    page: "brief",
    cadence: "overall",
    defect: d.defect,
    condition: driver.condition.key,
    label: itemLabel(PLANT_BRIEF_LABEL, `${d.label}, ${driver.condition.label}`),
  });
}

/** How many of the overall brief's defects the consultant's page offers as quick questions. */
export const QUICK_DEFECTS_MAX = 6;

/** The quick questions every plant gets, after its defects'. */
export const QUICK_GENERAL = ["What should we check first today?", "Compare this month with last month"] as const;

/** A quick question on the consultant's page: a click drafts it and sets its context (none for a general one). */
export interface QuickAsk {
  text: string;
  about: CopilotAbout | null;
}

/**
 * The consultant page's quick questions: one per defect of the overall brief, largest first, at most six, each about
 * its defect; then the general ones, whose words name their periods (the server reads "today" and "this month").
 */
export function quickAsksOf(brief: Pick<PlantBrief, "defects"> | null | undefined): QuickAsk[] {
  const defects = [...(brief?.defects ?? [])].sort((a, b) => b.pieces - a.pieces).slice(0, QUICK_DEFECTS_MAX);
  return [
    ...defects.map((d) => ({ text: `What drives ${defectQuestionName(d)}?`, about: overallDefectAbout(d) })),
    ...QUICK_GENERAL.map((text) => ({ text, about: null })),
  ];
}

/** Where a period's brief is shown: the Briefs page, or a report that shows the brief of its window (the AI report). */
export type BriefAskPage = "brief" | "report";

const capital = (s: string) => `${s[0].toUpperCase()}${s.slice(1)}`;

/** "Day brief, 18 Aug 2026", "Week brief, Week 33, 10 to 16 Aug 2026": a period's brief on the Briefs page. */
export function periodBriefLabel(p: BriefPeriodRef | CadenceBrief): string {
  return `${capital(CADENCE_NOUN[p.cadence])} brief, ${windowLabel(p)}`;
}

/** A brief's name on the chip: its period's on the Briefs page, the report's with its days in a report. */
export function cadenceBriefLabel(b: CadenceBrief, page: BriefAskPage): string {
  return page === "report" ? `AI report, ${dayRangeText(b.window.first_day, b.window.last_day)}` : periodBriefLabel(b);
}

/**
 * The period inside a question: "on 18 Aug 2026", "in shift 3 of 18 Aug 2026", "in week 33, 10 to 16 Aug 2026",
 * "in Aug 2026", "in 2025", "from 1 to 31 Jul 2026".
 */
export function periodPhrase(b: CadenceBrief): string {
  const w = windowLabel(b);
  switch (b.cadence) {
    case "day":
      return `on ${w}`;
    case "span":
      return `from ${w}`;
    case "shift":
    case "week":
      return `in ${w.slice(0, 1).toLowerCase()}${w.slice(1)}`;
    default:
      return `in ${w}`;
  }
}

/** A period's brief as the Briefs page's context: the top bar's button opens with it (lib/copilot/ask launcherHref). */
export function periodBriefAbout(p: BriefPeriodRef | CadenceBrief): CopilotAbout {
  return aboutFor({ page: "brief", cadence: p.cadence, period: p.period_key, label: periodBriefLabel(p) });
}

/** The asks of one period's brief, each from its section. */
export interface CadenceAsks {
  header: () => Ask;
  pareto: (r: CadenceParetoRow) => Ask;
  mover: (m: CadenceMover) => Ask;
  excursion: (e: CadenceExcursion) => Ask;
  risk: (r: CadenceRisk) => Ask;
  actions: (bk: ActionBucket) => Ask;
}

/**
 * A period brief's asks. Each carries the brief's cadence and period key (the server hands the brain that brief); in a
 * report, its page is the report's and it carries the window's days too. `labelOf` names a defect as the brief does.
 */
export function cadenceAsksOf(b: CadenceBrief, page: BriefAskPage, labelOf: (defect: string) => string): CadenceAsks {
  const base = cadenceBriefLabel(b, page);
  const phrase = periodPhrase(b);
  const about = (extra: Partial<AboutInput>, item?: string): CopilotAbout =>
    aboutFor({
      page,
      cadence: b.cadence,
      period: b.period_key,
      ...(page === "report" ? { from: b.window.first_day, to: b.window.last_day } : {}),
      ...extra,
      label: itemLabel(base, item),
    });
  const conditionOf = (i: number | undefined) => (i === undefined ? undefined : b.excursions[i]?.condition.key);
  return {
    header: () => ({ question: `What stood out ${phrase}, and what should we check first?`, about: about({}) }),
    pareto: (r) => ({
      question: `What is behind ${r.label} ${phrase}, and what should we check first?`,
      about: about({ defect: r.defect }, r.label),
    }),
    mover: (m) =>
      m.kind === "part"
        ? {
            question: `Why did part ${m.label} move outside its limits ${phrase}, and what should we check first?`,
            about: about({}, `Part ${m.label}`),
          }
        : m.kind === "line"
          ? {
              // A line's own limits (cadence/v13): no defect to name, the line is what moved.
              question: `Why did ${lineName(m.label)} move outside its own limits ${phrase}, and what should we check first?`,
              about: about({}, lineName(m.label)),
            }
          : {
            question: `Why did ${m.label} move outside its limits ${phrase}, and what should we check first?`,
            about: about({ defect: m.key }, m.label),
          },
    excursion: (e) => ({
      question: `${e.condition.label} ran ${excursionWay(e)} ${excursionWindow(e)} ${phrase}. What does that mean for the castings, and what should we check?`,
      about: about({ condition: e.condition.key }, excursionTitle(e)),
    }),
    risk: (r) => ({
      question: `Which castings are at risk of ${r.label} ${phrase}, and what should we check before they ship?`,
      about: about({ defect: r.defect, condition: conditionOf(r.excursion) }, `${r.label} at risk`),
    }),
    actions: (bk) => {
      const label = labelOf(bk.first.defect);
      return {
        question: `Why is this on the list for ${label} ${phrase}, and how do we do it?`,
        about: about({ defect: bk.first.defect, condition: conditionOf(bk.first.excursion) }, `what to do for ${label}`),
      };
    },
  };
}
